import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft, ArrowRight, CalendarCheck, ChevronLeft, ChevronRight, CreditCard, Landmark, Lock, SlidersHorizontal, Sparkles,
} from 'lucide-react';
import { getPortalEmoji } from '../../utils/portalEmojis';

/**
 * Dummy, look-don't-touch mimics of the /start signup flow, shown under the
 * "Getting Started" 1-2-3 on the Assessment Analysis so a prospect can see
 * exactly what picking a start date, signing, paying and the portal unlocking
 * look like. Markup mirrors Checkout.js, ContractSigningReturn.js,
 * CheckoutReturn.js and PortalPendingStartGate.js — keep them in step.
 */

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const pad = (n) => String(n).padStart(2, '0');
const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;

// Same rule as Checkout.getMinStartDate: the next weekday after today.
const getMinStartDate = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  do {
    d.setDate(d.getDate() + 1);
  } while (isWeekend(d));
  return d;
};

// Dummy pick: the first Monday on/after the minimum start date.
const getDummyStartDate = () => {
  const d = getMinStartDate();
  while (d.getDay() !== 1) d.setDate(d.getDate() + 1);
  return d;
};

const formatDisplayDate = (d) =>
  d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

const START_NAV = [
  { id: 'dashboard', text: 'Dashboard' },
  { id: 'roadmap', text: 'Roadmap', locked: true },
  { id: 'courses', text: 'Courses', locked: true },
  { id: 'community', text: 'Community', locked: true },
  { id: 'sales', text: 'Sales', locked: true },
  { id: 'operations', text: 'Operations', locked: true },
  { id: 'technology', text: 'Technology', locked: true },
  { id: 'finance', text: 'Finance', locked: true },
  { id: 'marketing', text: 'Marketing', locked: true },
  { id: 'hr', text: 'HR', locked: true },
];

const TOUR_TILES = [
  { id: 'dashboard', title: 'Dashboard' },
  { id: 'roadmap', title: 'Roadmap' },
  { id: 'courses', title: 'Courses' },
  { id: 'tasks', title: 'Tasks' },
  { id: 'community', title: 'Community' },
  { id: 'sales', title: 'Sales' },
  { id: 'operations', title: 'Operations' },
  { id: 'technology', title: 'Technology' },
  { id: 'finance', title: 'Finance' },
  { id: 'marketing', title: 'Marketing' },
  { id: 'hr', title: 'HR' },
  { id: 'collection', title: 'Collection' },
];

/* ---------------- shared mock chrome ---------------- */

const PORTAL_NAV = TOUR_TILES.map((t) => ({ id: t.id, text: t.title }));

export const MockSidebar = ({ variant = 'start', activeId }) => (
  <aside className="hidden lg:flex w-52 flex-shrink-0 flex-col border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-4">
    <div className="px-4 mb-4 flex items-center gap-2">
      <img src="/bba-emblem.png" alt="" className="w-7 h-7 object-contain" />
      <span className="text-xs font-bold tracking-tight text-gray-900 dark:text-white leading-tight">
        Builders Breakthrough
      </span>
    </div>
    <nav className="px-2 space-y-0.5">
      {(variant === 'portal' ? PORTAL_NAV : START_NAV).map((item, i) => {
        const locked = variant === 'start' && item.locked;
        const active = activeId ? item.id === activeId : i === 0;
        return (
          <div
            key={item.id}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm ${
              active
                ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-semibold'
                : locked
                  ? 'text-gray-400 dark:text-gray-500'
                  : 'text-gray-700 dark:text-gray-200'
            }`}
          >
            <span className="text-base leading-none">{getPortalEmoji(item.id)}</span>
            <span className="flex-1 truncate">{item.text}</span>
            {locked && <Lock className="w-3.5 h-3.5" />}
          </div>
        );
      })}
    </nav>
  </aside>
);

const CheckoutProgress = ({ currentStep }) => (
  <div className="flex justify-center mb-8">
    <div className="flex items-center space-x-4 sm:space-x-8">
      {[
        [1, 'Confirm Program'],
        [2, 'Start Date & Contract'],
        [3, 'Checkout'],
      ].map(([n, label], i) => (
        <React.Fragment key={n}>
          {i > 0 && <div className={`w-8 sm:w-16 h-1 ${currentStep >= n ? 'bg-red-600' : 'bg-gray-300'}`} />}
          <div className="flex items-center">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                currentStep >= n ? 'bg-red-600 text-white' : 'text-gray-400 bg-gray-300 dark:bg-gray-600'
              }`}
            >
              {n}
            </div>
            <div className="ml-3 hidden sm:block">
              <p className={`text-sm font-medium ${currentStep >= n ? 'text-red-600' : 'text-gray-500'}`}>{label}</p>
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  </div>
);

const CheckoutShell = ({ currentStep, children, footer }) => (
  <div className="min-h-full bg-gray-50 dark:bg-gray-900 py-8 px-4">
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <span className="inline-flex items-center justify-center w-10 h-10 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full">
          <ArrowLeft className="h-4 w-4" />
        </span>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Complete Signup</h1>
        <div className="w-10" />
      </div>
      <CheckoutProgress currentStep={currentStep} />
      <div className="max-w-4xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
          {children}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">{footer}</div>
        </div>
      </div>
    </div>
  </div>
);

const DEFAULT_PROGRAM_CARDS = [
  { name: 'Core', price: '$1,500', isMostPopular: false, isSuggested: false, features: [{ icon: '🗺️', text: 'Custom roadmap' }, { icon: '📞', text: 'Monthly strategy call' }, { icon: '🎓', text: 'Course library' }] },
  { name: 'Pro', price: '$2,500', isMostPopular: true, isSuggested: true, features: [{ icon: '🗺️', text: 'Custom roadmap' }, { icon: '📞', text: 'Bi-weekly strategy calls' }, { icon: '⚙️', text: 'SOP & systems build-out' }, { icon: '🤖', text: 'JobTread optimization' }] },
  { name: 'Premier', price: '$4,000', isMostPopular: false, isSuggested: false, features: [{ icon: '🗺️', text: 'Custom roadmap' }, { icon: '📞', text: 'Weekly strategy calls' }, { icon: '👥', text: 'Hiring support' }, { icon: '💵', text: 'Finance coaching' }] },
];

// Mirrors src/pages/Program.js — header, billing toggle, suggested-program banner, cards.
const ProgramScreen = ({ programCards }) => {
  const cards = programCards && programCards.length ? programCards : DEFAULT_PROGRAM_CARDS;
  return (
    <div className="min-h-full bg-[#f4f2ef] dark:bg-[#17140f]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Choose Your Ideal Growth Plan</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Pick a program below, or build your own from scratch.</p>
        </div>
        <div className="flex justify-center mb-6">
          <div className="inline-flex rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-1">
            {[
              ['Monthly', null, true],
              ['Annual', 10, false],
            ].map(([name, discount, active]) => (
              <span
                key={name}
                className={`inline-flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-medium ${
                  active ? 'bg-red-600 text-white' : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                {name}
                {discount && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                    Save {discount}%
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
        {/* Suggested Program Banner */}
        <div className="w-full mb-6">
          <div className="w-full rounded-xl border border-green-200 dark:border-green-900 bg-white dark:bg-gray-900 shadow-sm px-5 py-4 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
            <span className="h-9 w-9 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-5 w-5" />
            </span>
            <span className="font-semibold text-gray-900 dark:text-white">Your BBA team has suggested a program for you</span>
            <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold">
              See program
              <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </div>
        {/* Program Cards */}
        <div className={`grid grid-cols-1 gap-6 mb-6 pt-4 overflow-visible ${cards.length === 1 ? 'max-w-sm mx-auto' : 'md:grid-cols-3'}`}>
          {cards.map((card) => (
            <div
              key={card.name}
              className={`relative overflow-visible rounded-xl border bg-white dark:bg-gray-900 shadow-sm h-full flex flex-col ${
                card.isMostPopular ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200 dark:border-gray-800'
              }`}
            >
              {card.isMostPopular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap">
                  <span className="bg-red-600 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-sm">Most Popular</span>
                </div>
              )}
              <div className="p-6 flex flex-col h-full">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-4">{card.name}</h3>
                <div className="text-center mb-5">
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">{card.price}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 ml-1.5">/month</span>
                  </div>
                </div>
                <span className="w-full py-2.5 px-6 rounded-lg font-semibold bg-red-600 text-white text-center mb-5 mt-auto">See Details</span>
                {card.features && card.features.length > 0 && (
                  <div className="space-y-2 flex-1">
                    {card.features.map((f, i) => (
                      <div key={i} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 text-left">
                        <span className="text-base">{f.icon || '⭐'}</span>
                        <span className="text-sm text-gray-700 dark:text-gray-300">{f.text}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-4 flex flex-wrap items-center justify-center gap-1.5">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Includes more than featured items above</span>
                  <span className="text-xs font-medium text-red-600 dark:text-red-400 underline">see more</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Customize Your Own Plan */}
        <div className="mb-8">
          <div className="w-full rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm px-5 py-4 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 text-center">
            <span className="h-9 w-9 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center flex-shrink-0">
              <SlidersHorizontal className="h-5 w-5" />
            </span>
            <p className="font-semibold text-gray-900 dark:text-white">Don&apos;t see a program that fits your vision?</p>
            <span className="inline-flex items-center px-4 py-2 rounded-lg border border-red-600 text-red-600 dark:text-red-400 text-sm font-semibold">Build your own</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------------- screens ---------------- */

// Mirrors Checkout.js step 1 — "Confirm Your Program".
const ConfirmProgramScreen = ({ programName, price, billingCycle, minimumTerm }) => (
  <CheckoutShell
    currentStep={1}
    footer={
      <>
        <span className="px-6 py-2 rounded-lg font-medium bg-gray-300 text-gray-500">Previous</span>
        <span className="px-6 py-2 rounded-lg font-medium bg-red-600 text-white">Continue</span>
      </>
    }
  >
    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Confirm Your Program</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {[
        ['Program', programName],
        [billingCycle === 'monthly' ? 'Monthly Total' : 'Total', price],
        ['Billing Cycle', billingCycle],
        ['Minimum Term', `${minimumTerm} months`],
      ].map(([label, value]) => (
        <div key={label} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 text-center">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">{label}</h3>
          <p className="text-lg font-semibold text-gray-900 dark:text-white capitalize">{value}</p>
        </div>
      ))}
    </div>
  </CheckoutShell>
);

const StartDateScreen = ({ startDate }) => {
  const minDate = getMinStartDate();
  const year = startDate.getFullYear();
  const month = startDate.getMonth();
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [
    ...Array.from({ length: firstDayOfWeek }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1)),
  ];
  const selectedKey = startDate.toDateString();
  return (
    <CheckoutShell
      currentStep={2}
      footer={
        <>
          <span className="px-6 py-2 rounded-lg font-medium bg-gray-600 text-white">Previous</span>
          <span className="px-6 py-2 rounded-lg font-medium bg-red-600 text-white">Sign Contract with DocuSign</span>
        </>
      }
    >
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Start Date &amp; Contract</h2>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">
            Program Start Date *
          </label>
          <div className="border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="p-1.5 rounded-md text-gray-300 dark:text-gray-600">
                <ChevronLeft size={20} />
              </span>
              <div className="text-sm font-semibold text-gray-900 dark:text-white text-center">
                {startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </div>
              <span className="p-1.5 rounded-md text-gray-600 dark:text-gray-300">
                <ChevronRight size={20} />
              </span>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {WEEKDAY_LABELS.map((l) => (
                <div key={l} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1">
                  {l}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((date, i) => {
                if (!date) return <div key={`e-${i}`} />;
                const disabled = isWeekend(date) || date < minDate;
                const selected = date.toDateString() === selectedKey;
                return (
                  <div
                    key={date.toISOString()}
                    className={`h-9 rounded-md text-sm font-medium flex items-center justify-center ${
                      selected
                        ? 'bg-red-600 text-white'
                        : disabled
                          ? 'text-gray-300 dark:text-gray-600'
                          : 'text-gray-900 dark:text-gray-100'
                    }`}
                  >
                    {date.getDate()}
                  </div>
                );
              })}
            </div>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center">
            Start date must be a weekday (Monday-Friday) and at least 1 weekday from today.
          </p>
        </div>
        <div className="w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <CalendarCheck size={18} className="text-red-600" />
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Billing Starts</h3>
          </div>
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            You will not be billed until {formatDisplayDate(startDate)}
          </p>
        </div>
      </div>
    </CheckoutShell>
  );
};

const DocuSignScreen = ({ companyName, signerName, startDate, programName, price }) => (
  <div className="min-h-full bg-[#f4f4f4] dark:bg-gray-950">
    {/* hosted-signing chrome */}
    <div className="bg-[#1e1e1e] text-white px-5 py-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <span className="text-[#ffcc22] font-black tracking-tight text-lg">docusign</span>
        <span className="text-xs text-gray-300 hidden sm:inline">Builders Breakthrough Academy Service Agreement</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-300 hidden sm:inline">Other Actions ▾</span>
        <span className="px-4 py-1.5 rounded bg-[#ffcc22] text-[#1e1e1e] text-xs font-bold">FINISH</span>
      </div>
    </div>
    <div className="bg-[#2a2a2a] text-gray-200 px-5 py-2 text-xs flex items-center justify-between">
      <span>Please review &amp; act on these documents.</span>
      <span className="hidden sm:inline">Fields remaining: 1</span>
    </div>
    {/* document */}
    <div className="max-w-3xl mx-auto py-8 px-4">
      <div className="relative bg-white dark:bg-gray-900 shadow-lg border border-gray-200 dark:border-gray-800 p-10 text-gray-800 dark:text-gray-200">
        <div className="absolute -left-2 top-[62%] flex items-center">
          <span className="bg-[#ffcc22] text-[#1e1e1e] text-[11px] font-bold px-2 py-1 rounded-r shadow">Sign</span>
          <span className="w-0 h-0 border-y-[10px] border-y-transparent border-l-[10px] border-l-[#ffcc22]" />
        </div>
        <p className="text-[11px] tracking-[0.2em] uppercase text-gray-400 text-center">Builders Breakthrough Academy</p>
        <h3 className="mt-2 text-xl font-bold text-center">Service Agreement</h3>
        <p className="mt-6 text-sm leading-relaxed">
          This Service Agreement is entered into between Builders Breakthrough Academy (&quot;BBA&quot;) and{' '}
          <span className="font-semibold">{companyName}</span> (&quot;Client&quot;) for the{' '}
          <span className="font-semibold">{programName}</span> program at <span className="font-semibold">{price}</span>,
          beginning <span className="font-semibold">{formatDisplayDate(startDate)}</span>.
        </p>
        <div className="mt-4 space-y-2">
          {[92, 100, 96, 84, 100, 72].map((w, i) => (
            <div key={i} className="h-2.5 rounded bg-gray-200 dark:bg-gray-700" style={{ width: `${w}%` }} />
          ))}
        </div>
        <p className="mt-6 text-sm font-semibold">1. Term &amp; Billing</p>
        <div className="mt-2 space-y-2">
          {[100, 88, 94].map((w, i) => (
            <div key={i} className="h-2.5 rounded bg-gray-200 dark:bg-gray-700" style={{ width: `${w}%` }} />
          ))}
        </div>
        <div className="mt-8 grid grid-cols-2 gap-8">
          <div>
            <div className="h-12 rounded border-2 border-[#ffcc22] bg-[#fff8dc] dark:bg-yellow-900/20 flex items-center px-3 text-sm italic text-gray-500">
              Sign here
            </div>
            <p className="mt-1 text-xs text-gray-500">{signerName}, {companyName}</p>
          </div>
          <div>
            <div className="h-12 rounded border border-gray-200 dark:border-gray-700 flex items-center px-3 text-sm text-gray-400">
              {new Date().toLocaleDateString('en-US')}
            </div>
            <p className="mt-1 text-xs text-gray-500">Date signed</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const PaymentScreen = ({ programName, price, billingText }) => (
  <CheckoutShell
    currentStep={3}
    footer={
      <>
        <span className="px-6 py-2 rounded-lg font-medium bg-gray-600 text-white">Previous</span>
        <div className="w-24" />
      </>
    }
  >
    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Payment</h2>
    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
      <p className="text-lg font-semibold text-gray-900 dark:text-white">
        {programName} — {price}
        <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">({billingText})</span>
      </p>
    </div>
    <div className="flex flex-col sm:flex-row gap-3">
      <span className="flex-1 py-3 px-4 rounded-lg border-2 font-medium flex flex-col items-center gap-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300">
        <Landmark className="w-6 h-6" />
        <span>Pay with Bank Account (ACH)</span>
      </span>
      <span className="flex-1 py-3 px-4 rounded-lg border-2 font-medium flex flex-col items-center gap-2 border-red-500 bg-red-600 text-white">
        <CreditCard className="w-6 h-6" />
        <span>Pay with Credit Card (3% Fee)</span>
      </span>
    </div>
  </CheckoutShell>
);

const PendingStartScreen = ({ startDate, countdown }) => {
  const steps = [
    { emoji: '📅', title: 'Schedule Your Kickoff Call', description: 'Pick a time with your success manager. This is where we set the plan for your first 90 days.', action: 'Pick a time' },
    { emoji: '🚀', title: 'Portal Kickoff', description: 'Rank your priorities across every department, then set up your profile and company info.', action: 'Start kickoff' },
    { emoji: '🗺️', title: 'Portal Tour', description: 'Take the page-by-page tour so nothing in the portal is a surprise on day one.', action: 'Start tour' },
  ];
  const tiles = [
    ['Days', countdown.days],
    ['Hours', countdown.hours],
    ['Minutes', countdown.minutes],
    ['Seconds', countdown.seconds],
  ];
  return (
    <div className="min-h-full p-6 text-gray-900 dark:text-gray-100 bg-[#f4f2ef] dark:bg-[#17140f]">
      <div className="max-w-4xl mx-auto space-y-8">
        <header className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
            <span className="text-4xl leading-none">🚀</span>
          </div>
          <h1 className="text-3xl font-bold text-center">Your Program Starts Soon!</h1>
          <p className="mt-2 text-center text-gray-500 dark:text-gray-400">
            Welcome aboard! Your portal unlocks on{' '}
            <span className="font-semibold text-gray-900 dark:text-gray-100">{formatDisplayDate(startDate)}</span>.
          </p>
        </header>
        <div className="flex justify-center gap-3">
          {tiles.map(([label, value]) => (
            <div key={label} className="w-20 sm:w-24 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow p-3 text-center">
              <div className="text-3xl font-bold tabular-nums">{pad(value)}</div>
              <div className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
            </div>
          ))}
        </div>
        <section>
          <header className="text-center mb-4">
            <h2 className="text-2xl font-semibold text-center">Getting Started</h2>
            <p className="mt-1 text-sm text-center text-gray-500 dark:text-gray-400">
              Three things to knock out before your start date — book your kickoff call, complete your portal kickoff, then take a page-by-page tour.
            </p>
          </header>
          <div className="grid gap-4 md:grid-cols-3">
            {steps.map((step, idx) => (
              <div key={step.title} className="rounded-lg border p-4 bg-white border-gray-200 dark:bg-gray-800/50 dark:border-gray-700 relative flex flex-col items-center text-center gap-2 pt-8 pb-5">
                <span className="absolute top-2 left-2 w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {idx + 1}
                </span>
                <span className="text-3xl leading-none">{step.emoji}</span>
                <h3 className="text-base font-semibold text-center">{step.title}</h3>
                <p className="text-xs text-center text-gray-500 dark:text-gray-400">{step.description}</p>
                <span className="mt-auto pt-3 inline-flex items-center gap-1 text-sm font-semibold text-red-600">
                  {step.action} <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            ))}
          </div>
        </section>
        <section>
          <header className="text-center mb-4">
            <h2 className="text-2xl font-semibold text-center">Portal Tour</h2>
            <p className="mt-1 text-sm text-center text-gray-500 dark:text-gray-400">
              A page-by-page look at your new portal. Jump to any stop — they unlock for real on your start date.
            </p>
          </header>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {TOUR_TILES.map((stop, idx) => (
              <div key={stop.id} className="rounded-lg border p-4 bg-white border-gray-200 dark:bg-gray-800/50 dark:border-gray-700 relative flex flex-col items-center justify-center py-5 gap-2">
                <span className="absolute top-2 left-2 w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {idx + 1}
                </span>
                <span className="text-3xl leading-none">{getPortalEmoji(stop.id)}</span>
                <span className="text-sm font-medium text-center">{stop.title}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

/* ---------------- preview shell ---------------- */

const StartFlowPreview = ({ companyName = 'Your Company', signerName = 'Owner', programName = 'Pro', price = '$2,500', billingText = 'per month', billingCycle = 'monthly', minimumTerm = 12, programCards = null, stepIndex = null, onStepChange = null }) => {
  const startDate = useMemo(() => getDummyStartDate(), []);
  const countdown = useMemo(() => {
    const diff = Math.max(0, startDate.getTime() - Date.now());
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor(diff / 3600000) % 24,
      minutes: Math.floor(diff / 60000) % 60,
      seconds: Math.floor(diff / 1000) % 60,
    };
  }, [startDate]);

  const STEPS = useMemo(
    () => [
      {
        n: 1,
        title: 'Pick a Start Date',
        screens: [
          {
            id: 'program',
            label: 'Programs',
            url: 'app.buildersbreakthrough.com/start/program',
            caption: 'The start portal lists every program. When a program has been suggested here, the green banner takes them straight to it with the deliverables already picked.',
            sidebar: 'start',
            render: () => <ProgramScreen programCards={programCards} />,
          },
          {
            id: 'confirm',
            label: 'Confirm',
            url: 'app.buildersbreakthrough.com/start/checkout',
            caption: 'Checkout opens on step 1 with the program, total, billing cycle, and minimum term to confirm before anything else.',
            sidebar: 'start',
            render: () => <ConfirmProgramScreen programName={programName} price={price} billingCycle={billingCycle} minimumTerm={minimumTerm} />,
          },
          {
            id: 'date',
            label: 'Choose a date',
            url: 'app.buildersbreakthrough.com/start/checkout',
            caption: 'Weekends and anything before the next business day are greyed out. Billing does not begin until the chosen date.',
            sidebar: 'start',
            render: () => <StartDateScreen startDate={startDate} />,
          },
        ],
      },
      {
        n: 2,
        title: 'Sign Contract in Portal',
        screens: [
          {
            id: 'docusign',
            label: 'DocuSign',
            url: 'na4.docusign.net/Signing',
            caption: 'The Sign Contract button opens the agreement in DocuSign, pre-filled with the program, price, and start date.',
            sidebar: null,
            render: () => <DocuSignScreen companyName={companyName} signerName={signerName} startDate={startDate} programName={programName} price={price} />,
          },
          {
            id: 'payment',
            label: 'Payment',
            url: 'app.buildersbreakthrough.com/start/checkout',
            caption: 'Pay by bank transfer or card through Stripe. The card is saved, but nothing is charged until the start date.',
            sidebar: 'start',
            render: () => <PaymentScreen programName={programName} price={price} billingText={billingText} />,
          },
        ],
      },
      {
        n: 3,
        title: 'Portal Unlocks',
        screens: [
          {
            id: 'unlocked',
            label: 'Portal',
            url: 'app.buildersbreakthrough.com/portal',
            caption: 'The portal opens right away with a countdown, the kickoff steps, and a tour of every page. Everything unlocks fully on the start date.',
            sidebar: 'portal',
            render: () => <PendingStartScreen startDate={startDate} countdown={countdown} />,
          },
        ],
      },
    ],
    [companyName, signerName, programName, price, billingText, billingCycle, minimumTerm, startDate, countdown, programCards]
  );

  return <FlowPreviewShell steps={STEPS} stepIndex={stepIndex} onStepChange={onStepChange} showStepTabs={false} />;
};

/**
 * Step tabs + browser frame + back/next for any list of mock screens.
 * steps: [{ n, badge?, title, screens: [{ id, label, url, caption, sidebar, sidebarActive?, render }] }]
 */
export const FlowPreviewShell = ({ steps: STEPS, stepIndex = null, onStepChange = null, showStepTabs = true }) => {
  const [pos, setRawPos] = useState({ step: stepIndex ?? 0, screen: 0 });
  // Parent-driven step (e.g. the big 1-2-3 cards above the preview)
  useEffect(() => {
    if (stepIndex !== null && stepIndex !== undefined) {
      setRawPos((prev) => (prev.step === stepIndex ? prev : { step: stepIndex, screen: 0 }));
    }
  }, [stepIndex]);
  const setPos = (next) => {
    setRawPos(next);
    if (onStepChange && next.step !== pos.step) onStepChange(next.step);
  };
  const step = STEPS[pos.step];
  const screen = step.screens[pos.screen];
  const flat = STEPS.flatMap((s, si) => s.screens.map((_, ci) => ({ step: si, screen: ci })));
  const flatIndex = flat.findIndex((f) => f.step === pos.step && f.screen === pos.screen);
  const goto = (i) => setPos(flat[Math.max(0, Math.min(flat.length - 1, i))]);

  const navBtn = 'inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';

  return (
    <div>
      {/* step tabs */}
      {showStepTabs && (
      <div className="flex justify-center mb-4">
        <div className="inline-flex flex-wrap items-center justify-center gap-1 p-1 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-600">
          {STEPS.map((s, si) => (
            <button
              key={s.n}
              type="button"
              onClick={() => setPos({ step: si, screen: 0 })}
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                pos.step === si ? 'bg-red-600 text-white shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <span className={`w-5 h-5 rounded-full text-[11px] font-bold flex items-center justify-center ${pos.step === si ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-700'}`}>
                {s.badge || s.n}
              </span>
              {s.title}
            </button>
          ))}
        </div>
      </div>
      )}

      {/* browser frame */}
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-400" />
            <span className="w-3 h-3 rounded-full bg-amber-400" />
            <span className="w-3 h-3 rounded-full bg-emerald-400" />
          </div>
          <div className="flex-1 flex items-center gap-2 px-3 py-1 rounded-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 truncate">
            <Lock className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{screen.url}</span>
          </div>
          <span className="hidden sm:inline text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">Preview</span>
        </div>
        <div className="flex h-[640px]">
          {screen.sidebar && <MockSidebar variant={screen.sidebar} activeId={screen.sidebarActive} />}
          <div className="flex-1 min-w-0 overflow-y-auto [&_button]:pointer-events-none select-none">
            {screen.render()}
          </div>
        </div>
      </div>

      {/* screen navigation + caption */}
      <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
        <button type="button" onClick={() => goto(flatIndex - 1)} disabled={flatIndex === 0} className={navBtn}>
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </button>
        <div className="flex items-center gap-1.5">
          {step.screens.map((s, ci) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setPos({ step: pos.step, screen: ci })}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                pos.screen === ci
                  ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white'
                  : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => goto(flatIndex + 1)} disabled={flatIndex === flat.length - 1} className={navBtn}>
          Next <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
      <p className="mt-3 text-sm text-center text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
        <span className="font-semibold text-gray-700 dark:text-gray-200">Step {step.n} · {screen.label}.</span> {screen.caption}
      </p>
    </div>
  );
};

export default StartFlowPreview;
